import { AfterViewInit, Component, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { AuthService } from "src/app/services/auth.service";
import { MatDialog } from '@angular/material/dialog';
import { RoleColorPickerComponent } from "../../Colorpicker/RoleColorPicker.component";
import { RoleService } from "src/app/services/role.service";
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmDialogComponent } from '../../ConfirmDialog/ConfirmDialog.component';

@Component({
  selector: "app-user-manager",
  templateUrl: "./user-manager.component.html",
  styleUrls: ["./user-manager.component.css"],
})
export class UserManagerComponent implements AfterViewInit {

  // Default colors for roles
  private defaultColors = {
    superadmin: '#dc2626', // red
    admin: '#2563eb',      // blue
    editor: '#059669',     // green
    member: '#7c3aed',     // purple
    restricted: '#6b7280', // gray
    controller: '#ea580c', // orange
    user: '#0891b2'        // cyan
  };

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  users: MatTableDataSource<any>;
  columnsToDisplay = ["firstname", "lastname", "username", "role", "id"];
  constructor(
    private roleServie: RoleService,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,  // Remove roleColorPicker from constructor
    private roleService: RoleService,
    private snackBar: MatSnackBar
  ) {
    this.users = new MatTableDataSource<any>([]);
  }

  // Add this method inside the component class
  async deleteUser(user: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you want to delete user ${user.username}?`,
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        try {
          await this.authService.deleteUser(user.id);
          this.users.data = this.users.data.filter(u => u.id !== user.id);
          this.snackBar.open('User deleted successfully', 'Close', {
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'top'
          });
        } catch (error) {
          console.error('Error deleting user:', error);
          this.snackBar.open('Error deleting user', 'Close', {
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'top'
          });
        }
      }
    });
  }

  async openColorPicker(role: string): Promise<void> {
    try {
      // Refresh roles before opening the picker
      const rolesData = await this.roleServie.getRolesP();
      this.roles = Array.isArray(rolesData) ? rolesData : [];

      const dialogRef = this.dialog.open(RoleColorPickerComponent, {
        width: '300px',
        data: {
          role: role,
          currentColor: this.roleColors[role.toLowerCase()] || '#6b7280'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.roleColors[role.toLowerCase()] = result;
          localStorage.setItem('roleColors', JSON.stringify(this.roleColors));
        }
      });
    } catch (error) {
      console.error('Error refreshing roles:', error);
    }
  }

  roles: string[] = [];
  roleColors: { [key: string]: string } = {};

  async ngOnInit() {
    try {
      // Cast the result to string[] to ensure type safety
      const rolesData = await this.roleServie.getRolesP();
      this.roles = Array.isArray(rolesData) ? rolesData : [];

      // Load saved colors if they exist
      const savedColors = localStorage.getItem('roleColors');
      if (savedColors) {
        this.roleColors = JSON.parse(savedColors);
      } else {
        // Initialize colors for all roles
        this.roles.forEach(role => {
          const lowerRole = role.toLowerCase();
          this.roleColors[lowerRole] = this.defaultColors[lowerRole] || '#6b7280';
        });
        // Save initial colors to localStorage
        localStorage.setItem('roleColors', JSON.stringify(this.roleColors));
      }
    } catch (error) {
      console.error('Error initializing role colors:', error);
      this.roles = []; // Ensure roles is always an array
    }
  }

  ngAfterViewInit() {
    this.authService.getUsers().then((data: any) => {
      const mappedData = data.map(user => ({
        ...user,
      }));

      // Filter out known users if needed
      const filteredData = mappedData.filter(e => !e.known);

      // Set the data after configuring paginator and sort
      this.users.data = filteredData;

      // Configure paginator and sort
      this.users.paginator = this.paginator;
      this.users.sort = this.sort;

      // Configure filter predicate
      this.users.filterPredicate = (data: any, filter: string) => {
        const searchText = filter.toLowerCase();
        return data.firstname?.toLowerCase().includes(searchText) ||
          data.lastname?.toLowerCase().includes(searchText) ||
          data.username?.toLowerCase().includes(searchText) ||
          data.role?.toLowerCase().includes(searchText);
      };
    });
  }

  public doFilter = (value: string) => {
    this.users.filter = value.trim().toLocaleLowerCase();
  };

  navigateDetail(row) {
    const url = `userEditor/` + row.id;
    this.router.navigate([url]);
  }

  sortData(sort: Sort) {
    if (!sort.active || sort.direction === '') {
      return;
    }

    this.users.data = this.users.data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'firstname': return compare(a.firstname, b.firstname, isAsc);
        case 'lastname': return compare(a.lastname, b.lastname, isAsc);
        case 'username': return compare(a.username, b.username, isAsc);
        case 'role': return compare(a.role, b.role, isAsc);
        default: return 0;
      }
    });
  }
}

function compare(a: string | number, b: string | number, isAsc: boolean) {
  if (typeof a === 'string' && typeof b === 'string') {
    return (a.toLowerCase() < b.toLowerCase() ? -1 : 1) * (isAsc ? 1 : -1);
  }
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}